import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  SelectChangeEvent,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Vocabulary } from "../../Utils/Vocabulary";

interface DateRangePickerProps {
  onDateRangeChange: (dateRange: DateRangeValue) => void;
  showGroupBy?: boolean;
  onGroupByChange?: (groupBy: string) => void;
}

export interface DateRangeValue {
  preset?: string;
  startDate?: string;
  endDate?: string;
  groupBy?: string;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  onDateRangeChange,
  showGroupBy = false,
  onGroupByChange,
}) => {
  const [preset, setPreset] = useState<string>("LAST_30_DAYS");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [groupBy, setGroupBy] = useState<string>("day");

  const handlePresetChange = (event: SelectChangeEvent) => {
    const newPreset = event.target.value;
    setPreset(newPreset);

    if (newPreset !== "CUSTOM") {
      setStartDate("");
      setEndDate("");
      onDateRangeChange({ preset: newPreset, groupBy });
    }
  };

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = event.target.value;
    setStartDate(newStartDate);

    if (newStartDate && endDate) {
      onDateRangeChange({ startDate: newStartDate, endDate, groupBy });
    }
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = event.target.value;
    setEndDate(newEndDate);

    if (startDate && newEndDate) {
      onDateRangeChange({ startDate, endDate: newEndDate, groupBy });
    }
  };

  const handleGroupByChange = (event: SelectChangeEvent) => {
    const newGroupBy = event.target.value;
    setGroupBy(newGroupBy);

    if (onGroupByChange) {
      onGroupByChange(newGroupBy);
    }

    // Trigger date range change with new groupBy
    if (preset !== "CUSTOM") {
      onDateRangeChange({ preset, groupBy: newGroupBy });
    } else if (startDate && endDate) {
      onDateRangeChange({ startDate, endDate, groupBy: newGroupBy });
    }
  };

  const handleRefresh = () => {
    if (preset !== "CUSTOM") {
      onDateRangeChange({ preset, groupBy });
    } else if (startDate && endDate) {
      onDateRangeChange({ startDate, endDate, groupBy });
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>{Vocabulary.dateRange}</InputLabel>
          <Select value={preset} label={Vocabulary.dateRange} onChange={handlePresetChange}>
            <MenuItem value="LAST_7_DAYS">{Vocabulary.last7Days}</MenuItem>
            <MenuItem value="LAST_30_DAYS">{Vocabulary.last30Days}</MenuItem>
            <MenuItem value="LAST_90_DAYS">{Vocabulary.last90Days}</MenuItem>
            <MenuItem value="THIS_MONTH">{Vocabulary.thisMonth}</MenuItem>
            <MenuItem value="LAST_MONTH">{Vocabulary.lastMonth}</MenuItem>
            <MenuItem value="THIS_YEAR">{Vocabulary.thisYear}</MenuItem>
            <MenuItem value="CUSTOM">{Vocabulary.customRange}</MenuItem>
          </Select>
        </FormControl>

        {preset === "CUSTOM" && (
          <>
            <TextField
              label={Vocabulary.startDate}
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            <TextField
              label={Vocabulary.endDate}
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
          </>
        )}

        {showGroupBy && (
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>{Vocabulary.groupBy}</InputLabel>
            <Select value={groupBy} label={Vocabulary.groupBy} onChange={handleGroupByChange}>
              <MenuItem value="day">{Vocabulary.daily}</MenuItem>
              <MenuItem value="week">{Vocabulary.weekly}</MenuItem>
              <MenuItem value="month">{Vocabulary.monthly}</MenuItem>
              <MenuItem value="year">{Vocabulary.yearly}</MenuItem>
            </Select>
          </FormControl>
        )}

        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          sx={{ minWidth: 120 }}
        >
          {Vocabulary.refresh}
        </Button>
      </Stack>
    </Box>
  );
};

export default DateRangePicker;
